import {
  KoButton,
  KoText,
  KoTitle,
  KoToast,
  KoTag
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { RELEASE_GATE_ITEMS, GATE_APPROVER_ROLES } from '../constants';
import ReleaseSubNav from './ReleaseSubNav';
import styles from './releases.module.scss';

const MOCK_GATE = {
  checklist: {
    qa_approved: { checked: true, verifiedBy: 'Sarah Kim', verifiedAt: '2026-02-22T10:00:00Z' },
    uat_approved: { checked: true, verifiedBy: 'Emily Davis', verifiedAt: '2026-02-22T14:30:00Z' },
    security_scan: { checked: true, verifiedBy: 'QA Bot', verifiedAt: '2026-02-22T15:00:00Z' },
    migration_validated: { checked: true, verifiedBy: 'Bob Chen', verifiedAt: '2026-02-23T09:00:00Z' },
    rollback_plan: { checked: true, verifiedBy: 'John Doe', verifiedAt: '2026-02-23T09:30:00Z' },
    feature_flags: { checked: true, verifiedBy: 'Alice Park', verifiedAt: '2026-02-23T10:00:00Z' },
    monitoring_prepared: { checked: false },
    support_briefed: { checked: false },
    release_notes: { checked: false },
    incident_response: { checked: false }
  },
  approvals: {
    engineering_lead: { approved: true, approverName: 'John Doe', approvedAt: '2026-02-23T11:00:00Z' },
    qa_lead: { approved: false },
    product_owner: { approved: false }
  }
};

function ReleaseGate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { releaseId, workspaceId } = useParams();

  const [gateData, setGateData] = useState(null);

  const releaseBasePath = workspaceId
    ? `/workspace/${workspaceId}/development/release`
    : '/release';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Releases', path: releaseBasePath },
      { title: 'Release Gate' }
    ]));
    setGateData(MOCK_GATE);
  }, [workspaceId]);

  const toggleCheckItem = (itemKey) => {
    setGateData((prev) => {
      const currentChecked = prev.checklist[itemKey]?.checked;
      return {
        ...prev,
        checklist: {
          ...prev.checklist,
          [itemKey]: currentChecked
            ? { checked: false }
            : { checked: true, verifiedBy: 'You', verifiedAt: new Date().toISOString() }
        }
      };
    });
    KoToast.success('Checklist updated.');
  };

  const handleApproval = (role) => {
    setGateData((prev) => ({
      ...prev,
      approvals: {
        ...prev.approvals,
        [role]: { approved: true, approverName: 'You', approvedAt: new Date().toISOString() }
      }
    }));
    KoToast.success(`${role} approval recorded!`);
  };

  const handleDeploy = () => {
    KoToast.success('Deployed to production! Release notes email queued.');
    navigate(`${releaseBasePath}/${releaseId}/release-notes`);
  };

  const checklist = gateData?.checklist || {};
  const approvals = gateData?.approvals || {};
  const checkedCount = RELEASE_GATE_ITEMS.filter(item => checklist[item.key]?.checked).length;
  const allChecked = checkedCount === RELEASE_GATE_ITEMS.length;
  const allApproved = GATE_APPROVER_ROLES.every(role => approvals[role.key]?.approved);
  const isFullyApproved = allChecked && allApproved;

  const gateStatus = isFullyApproved ? 'approved' : allChecked ? 'ready' : 'locked';
  const gateStatusConfig = {
    locked: { color: 'default', label: 'Locked', icon: 'LockOutlined' },
    ready: { color: 'warning', label: 'Ready for Approval', icon: 'ClockCircleOutlined' },
    approved: { color: 'success', label: 'Approved for Production', icon: 'CheckCircleOutlined' }
  };
  const statusCfg = gateStatusConfig[gateStatus];

  return (
    <div className='tableCard'>
      <ReleaseSubNav activeKey='gate' />

      <KoToolBar title='Production Release Gate' search={{ visible: false }} filter={{ visible: false, options: [] }} buttonList={[]} />

      <div className={styles.flowBanner}>
        <KoIcon name='LockOutlined' width={14} height={14} />
        <KoText text='Step 4: Release Gate — Complete all checklist items and get approvals to deploy' />
      </div>

      <div className={styles.gateStatusHeader}>
        <div className={styles.gateStatusLeft}>
          <KoIcon name={statusCfg.icon} width={24} height={24} />
          <KoTitle level={4} text={statusCfg.label} />
          <KoTag color={statusCfg.color}>{checkedCount}/{RELEASE_GATE_ITEMS.length} Verified</KoTag>
        </div>
        <div className={styles.gateStatusRight}>
          {isFullyApproved && (
            <KoButton type='primary' size='large' label='Deploy to Production'
              icon={<KoIcon name='RocketOutlined' color='#FFF' width={16} height={16} />}
              onClick={handleDeploy} />
          )}
        </div>
      </div>

      <div className={styles.gateSection}>
        <KoText className={styles.sectionLabel} text='MANDATORY RELEASE READINESS CHECKLIST' />
        <div className={styles.gateChecklist}>
          {RELEASE_GATE_ITEMS.map((item) => {
            const itemData = checklist[item.key] || {};
            const isChecked = itemData.checked;
            return (
              <div key={item.key}
                className={`${styles.gateCheckItem} ${isChecked ? styles.gateCheckItemDone : ''}`}
                onClick={() => toggleCheckItem(item.key)}>
                <div className={styles.gateCheckBox}>
                  {isChecked
                    ? <KoIcon name='CheckCircleOutlined' width={20} height={20} color='var(--palette-success-main)' />
                    : <div className={styles.gateCheckBoxEmpty} />}
                </div>
                <div className={styles.gateCheckContent}>
                  <div className={styles.gateCheckLabel}>
                    <KoIcon name={item.icon} width={14} height={14} />
                    <KoText text={item.label} />
                  </div>
                  {isChecked && itemData.verifiedBy && (
                    <KoText className={styles.gateCheckMeta}
                      text={`Verified by ${itemData.verifiedBy} at ${new Date(itemData.verifiedAt).toLocaleString()}`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.gateSection}>
        <KoText className={styles.sectionLabel} text='REQUIRED APPROVALS' />
        <div className={styles.gateApprovals}>
          {GATE_APPROVER_ROLES.map((role) => {
            const approvalData = approvals[role.key] || {};
            const isApproved = approvalData.approved;
            return (
              <div key={role.key} className={`${styles.approvalCard} ${isApproved ? styles.approvalCardDone : ''}`}>
                <div className={styles.approvalCardHeader}>
                  <KoIcon name={role.icon} width={20} height={20} />
                  <KoTitle level={5} text={role.label} />
                </div>
                {isApproved ? (
                  <div className={styles.approvalCardBody}>
                    <KoTag color='success'>Approved</KoTag>
                    <KoText className={styles.approvalMeta} text={approvalData.approverName} />
                    {approvalData.approvedAt && (
                      <KoText className={styles.approvalTime} text={new Date(approvalData.approvedAt).toLocaleString()} />
                    )}
                  </div>
                ) : (
                  <div className={styles.approvalCardBody}>
                    <KoTag color='default'>Pending</KoTag>
                    <KoButton type='primary' label={`Approve as ${role.label}`}
                      onClick={() => handleApproval(role.key)} disabled={!allChecked}
                      className={styles.approvalBtn} />
                    {!allChecked && <KoText className={styles.approvalHint} text='Complete all checklist items first' />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={`${styles.deploySection} ${isFullyApproved ? styles.deploySectionReady : ''}`}>
        {isFullyApproved ? (
          <>
            <KoIcon name='CheckCircleOutlined' width={40} height={40} color='var(--palette-success-main)' />
            <KoTitle level={4} text='Release Gate Passed — Ready for Production!' />
            <KoText text='All checklist items verified and all approvals collected.' />
            <KoButton type='primary' size='large' label='Deploy to Production'
              icon={<KoIcon name='RocketOutlined' color='#FFF' width={16} height={16} />}
              onClick={handleDeploy} />
          </>
        ) : (
          <>
            <KoIcon name='LockOutlined' width={32} height={32} color='var(--palette-text-secondary)' />
            <KoTitle level={5} text='Production deployment is locked' />
            <KoText text={
              !allChecked
                ? `${RELEASE_GATE_ITEMS.length - checkedCount} checklist items remaining`
                : `${GATE_APPROVER_ROLES.filter(r => !approvals[r.key]?.approved).length} approvals pending`
            } />
          </>
        )}
      </div>
    </div>
  );
}

export default ReleaseGate;
